/**
 * m3 echo Frontend - Zitat-Anteil React Query Hooks
 *
 * Query-Keys enthalten Projekt, sämtliche Filter und den Identitäts-Scope.
 * Es gibt bewusst kein keepPreviousData: bei Filter- oder Kontowechsel wird nie
 * eine alte Antwort unter neuen Beschriftungen gezeigt.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '../services/api';
import { billingApi } from '../services/billing';
import { listAutopilots } from '../services/autopilots';
import { customAutopilotApi } from '../services/admin';
import { getIdentityScope, useIsAdmin } from '../stores/useAuthStore';
import type { CitationDomainsState, CitationFilters, ProjectCitations } from '../types/api';

export const CITATION_POLL_INTERVAL_MS = 20_000;

export const citationKeys = {
  project: (projectId: number) => ['citations', projectId] as const,
  metrics: (projectId: number, filters: CitationFilters) =>
    ['citations', projectId, 'metrics', filters, getIdentityScope()] as const,
  autopilots: (projectId: number, tenantId: number | undefined, isAdmin: boolean) =>
    ['citations', projectId, 'autopilots', tenantId ?? null, isAdmin, getIdentityScope()] as const,
  domains: (projectId: number) => ['citations', projectId, 'domains', getIdentityScope()] as const,
};

/**
 * Liefert true, wenn der Mandant das Modul Zitat-Anteil freigeschaltet hat (Fehler -> false).
 *
 * Mit `tenantId` prüfen Plattform-Admins den Mandanten des Projekts: Ihre eigene
 * Konfiguration (`my-config`) schaltet ohne Mandant alle Features frei, der Server
 * prüft das Modul aber am Projektmandanten.
 */
export const useCitationModuleActive = (tenantId?: number) => {
  const isAdmin = useIsAdmin();
  const projectTenant = isAdmin && tenantId !== undefined ? tenantId : undefined;
  const { data, isLoading } = useQuery({
    queryKey: projectTenant !== undefined
      ? ['billing', 'effective-config', projectTenant, getIdentityScope()]
      : ['billing', 'my-config', getIdentityScope()],
    queryFn: async () => (projectTenant !== undefined
      ? await billingApi.getEffectiveConfig(projectTenant)
      : await billingApi.getMyConfig()).data,
    staleTime: 30_000,
    retry: false,
  });
  return { active: data?.active_features?.citation_share === true, isLoading };
};

export const useProjectCitations = (
  projectId: number,
  filters: CitationFilters,
  enabled = true
) =>
  useQuery<ProjectCitations>({
    queryKey: citationKeys.metrics(projectId, filters),
    queryFn: async ({ signal }) =>
      (await projectsApi.getCitations(projectId, { ...filters }, signal)).data,
    enabled,
    retry: false,
    // Begrenztes Nachladen, solange die Quellenauflösung läuft; endet automatisch.
    refetchInterval: (query) =>
      query.state.data?.quality?.resolution_in_progress ? CITATION_POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
  });

export const useCitationDomains = (projectId: number, enabled = true) =>
  useQuery<CitationDomainsState>({
    queryKey: citationKeys.domains(projectId),
    queryFn: async () => (await projectsApi.getCitationDomains(projectId)).data,
    enabled,
    retry: false,
  });

export const useUpdateCitationDomains = (projectId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      expected_revision: string | null;
      domains: { domain: string; kind: string; match: string }[];
    }) => (await projectsApi.updateCitationDomains(projectId, data)).data as CitationDomainsState,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: citationKeys.project(projectId) });
    },
  });
};

export interface CitationAutopilotOption {
  /** `std:<id>` (Standard-Autopilot) oder `custom:<id>` (eigener Autopilot) */
  value: string;
  label: string;
}

const SCHEDULE_LABELS: Record<string, string> = { daily: 'täglich', weekly: 'wöchentlich', monthly: 'monatlich' };

/**
 * Autopilots, die als Filter angeboten werden dürfen: Standard-Autopilots nur des
 * Projekts (Server filtert nach Projekt und Mandant). Eigene Autopilots haben keine
 * Projektbindung und sind nur über die Admin-Schnittstelle lesbar; sie werden nur
 * Plattform-Admins und nur für den Mandanten des Projekts angeboten. Ob ein eigener
 * Autopilot Tests dieses Projekts hat, prüft der Server beim Filtern.
 */
export const useCitationAutopilotOptions = (projectId: number, tenantId: number | undefined, isAdmin: boolean) =>
  useQuery<CitationAutopilotOption[]>({
    queryKey: citationKeys.autopilots(projectId, tenantId, isAdmin),
    queryFn: async () => {
      const options: CitationAutopilotOption[] = [];
      const standard = await listAutopilots({ project_id: projectId });
      for (const ap of standard) {
        if (ap.project_id !== projectId) continue;
        const type = ap.autopilot_type === 'llm_score' ? 'LLM-Score' : 'Benchmark';
        options.push({
          value: `std:${ap.id}`,
          label: `${type}-Autopilot #${ap.id} (${SCHEDULE_LABELS[ap.schedule_type] ?? ap.schedule_type})`,
        });
      }
      if (isAdmin && tenantId !== undefined) {
        try {
          const custom = (await customAutopilotApi.list(200, 0)).data.autopilots;
          for (const ap of custom) {
            if (ap.tenant_id !== tenantId) continue;
            options.push({ value: `custom:${ap.id}`, label: `Eigener Autopilot ${ap.name || `#${ap.id}`}` });
          }
        } catch {
          // Die Liste eigener Autopilots ist optional; Standard-Autopilots bleiben nutzbar.
        }
      }
      return options;
    },
    retry: false,
    staleTime: 60_000,
  });
